<template>
  <div class="discount-pricing-grid">
    <div v-if="loading" class="text-center py-3">
      <i class="ti-reload rotate"></i> {{ $t('general.loading') }}
    </div>

    <template v-else>
      <div v-if="products.length" class="table-responsive-wrapper">
        <div class="table-horizontal-scroll">
          <table class="table pricing-table">
            <thead>
              <tr>
                <th class="product-col sticky-col">{{ $t('discountCampaigns.productStandardPrice') }}</th>
                <th
                  v-for="combo in combos"
                  :key="combo.key"
                  class="source-method-col">
                  <div class="combo-label">{{ combo.label }}</div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="product in products" :key="product.id">
                <td class="product-col sticky-col">
                  <div class="product-info">
                    <strong>{{ product.name }}</strong>
                    <div class="product-id text-muted">ID: {{ product.id }}</div>
                    <div class="standard-price text-muted">
                      {{ formatMoneyAmount(product.price) }}
                    </div>
                  </div>
                </td>
                <td
                  v-for="combo in combos"
                  :key="`${product.id}-${combo.key}`"
                  class="pricing-cell">
                  <div v-if="getCell(product.id, combo)" class="discount-cell">
                    <div class="discount-row">
                      <span class="discount-label">{{ $t('discountCampaigns.originalPrice') }}</span>
                      <span>{{ formatMoneyAmount(getCell(product.id, combo).price) }}</span>
                    </div>
                    <div class="discount-row">
                      <span class="discount-label">{{ $t('discountCampaigns.discountAmount') }}</span>
                      <span class="text-success">-{{ formatMoneyAmount(getCell(product.id, combo).discount) }}</span>
                    </div>
                    <div class="discount-row discount-row--bold">
                      <span class="discount-label">{{ $t('discountCampaigns.discountedPrice') }}</span>
                      <span>{{ formatMoneyAmount(getCell(product.id, combo).discounted_price) }}</span>
                    </div>
                    <div class="discount-row text-muted" v-if="getCell(product.id, combo).pricing_type">
                      <small>{{ getCell(product.id, combo).pricing_type === 'catalog' ? $t('discountCampaigns.pricingCatalog') : $t('discountCampaigns.pricingProfile') }}</small>
                    </div>
                    <div class="discount-row" v-if="getCell(product.id, combo).number_of_units != null">
                      <span class="discount-label">{{ $t('discountCampaigns.cap') }}</span>
                      <span>{{ getCell(product.id, combo).number_of_units }}</span>
                    </div>
                    <div class="discount-row" v-if="showUnitsSold">
                      <span class="discount-label">{{ $t('discountCampaigns.unitsSold') }}</span>
                      <span>{{ getCell(product.id, combo).units_sold || 0 }}</span>
                    </div>
                  </div>
                  <div v-else class="text-muted text-center">—</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else class="text-center py-4 text-muted">
        <p>{{ $t('gestiune.noData') }}</p>
      </div>

      <div class="pagination-wrapper" v-if="pagination && pagination.totalRecords > 0">
        <el-pagination
          layout="prev, pager, next"
          :total="pagination.totalRecords"
          :page-size="pagination.perPage"
          :current-page="pagination.page"
          @current-change="$emit('page-change', $event)"
          style="margin-top: 0; text-align: right; width: 100%;"
        />
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'DiscountCampaignPricingGrid',
  props: {
    products: {
      type: Array,
      default: () => []
    },
    pricingData: {
      type: Object,
      default: () => ({})
    },
    combos: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    showUnitsSold: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Object,
      default: null
    }
  },
  methods: {
    getCell (productId, combo) {
      const key = `${productId}_${combo.source}_${combo.method}`
      return this.pricingData[key] || null
    }
  }
}
</script>

<style scoped lang="scss">
.discount-pricing-grid {
  width: 100%;
}

.table-responsive-wrapper {
  overflow-x: auto;
  max-width: 100%;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.table-horizontal-scroll {
  overflow-x: auto;
}

.pricing-table {
  width: auto;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 0;
}

.pricing-table th,
.pricing-table td {
  border-right: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
  vertical-align: top;
}

.product-col {
  min-width: 200px;
  max-width: 250px;
  background-color: #fff;
  z-index: 10;
}

.sticky-col {
  position: sticky;
  left: 0;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  background-color: #fff;
}

.pricing-table thead .product-col {
  background-color: #f5f7fa;
  z-index: 20;
}

.product-info {
  padding: 10px;
}

.product-info strong {
  display: block;
  margin-bottom: 5px;
  color: #303133;
}

.product-id,
.standard-price {
  font-size: 12px;
}

.source-method-col {
  min-width: 150px;
  text-align: center;
  padding: 8px 4px !important;
  background-color: #f5f7fa;
}

.combo-label {
  font-weight: 600;
  color: #409eff;
  font-size: 11px;
  text-transform: uppercase;
  line-height: 1.3;
}

.pricing-cell {
  min-width: 150px;
  padding: 8px !important;
  background-color: #fff;
}

.discount-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
}

.discount-row {
  display: flex;
  justify-content: space-between;
  gap: 6px;
}

.discount-row--bold {
  font-weight: 600;
}

.discount-label {
  color: #909399;
  flex-shrink: 0;
}

.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  flex-wrap: wrap;
  gap: 10px;
}

.pagination-info {
  font-size: 13px;
  color: #606266;
}

.rotate {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/DiscountCampaigns/DiscountCampaignPricingGrid.vue